'use client';
import { FC } from 'react';
import Image from 'next/image';

import { Logo, Text } from '@/components';
import { PageType } from '@/layouts/Header';
import {
  COUNTRY_CODE,
  EMAIL_ID_SUPPORT,
  FACEBOOK_URL,
  HEADER_FOOTER_TYPES,
  INSTAGRAM_URL,
  LANDLINE_NUMBER,
  MENU_LINKS,
  WHATSAPP_NUMBER,
  YOUTUBE_URL,
} from '@/constants';
import Container from '../Container';
import Link from 'next/link';
import { useCart } from '@/context/cartContext';
import { usePathname } from 'next/navigation';
import { twMerge } from 'tailwind-merge';
import WhatsappFooter from '@/components/Whatsapp/Footer';
import { pushGA4Event } from '@/utils/helper';

interface PropTypes {
  type: PageType | string;
}

const Footer: FC<PropTypes> = ({ type = HEADER_FOOTER_TYPES.global }) => {
  const { cartItems } = useCart();
  const pathname = usePathname();
  const listItemClass = 'cursor-pointer hover:underline text-base font-medium';
  const listClass = 'flex flex-col gap-y-2.5 max-lg:hidden';
  const socialClass = 'text-sm font-bold cursor-pointer hover:underline';
  const headerClass =
    'text-lg font-semibold hover:underline cursor-pointer group-hover:underline';
  const subHeaderClass = 'text-sm font-normal text-wrap';
  const anchorClass = 'cursor-pointer font-semibold underline';
  const isPlpPage =
    pathname.includes('/shop/') && !pathname.includes('product');
  const addBottomPadding = isPlpPage && cartItems && cartItems.length > 0;
  const addBottomPaddingCheckout =
    pathname === '/checkout/home-delivery/' ||
    pathname === '/checkout/pickup-from-store/';
  const cookieText = `We use cookies to optimise your visit. By continuing to browse our site you are accepting our `;

  if (type === HEADER_FOOTER_TYPES.global) {
    return (
      <footer
        className={twMerge(
          'w-full bg-black text-white',
          addBottomPadding && 'max-lg:pb-[148px]'
        )}>
        <div className='mb-14 ml-18 mr-[9.13rem] flex flex-col items-center max-lg:ml-0 max-lg:mr-0 lg:mb-16'>
          <div className='flex w-full items-start justify-between max-lg:justify-center lg:mt-16'>
            <Logo rootClasses='w-72 max-lg:mt-8' color='white' />
            <ul className={listClass}>
              <li className={listItemClass}>
                <Link href={MENU_LINKS.SHOP} prefetch={false}>
                  Shop Online
                </Link>
              </li>
              {process.env.NEXT_PUBLIC_GIFTING_SECTION !== 'disable' && (
                <li className={listItemClass}>
                  <Link href='/gifting' prefetch={false}>
                    Gifting
                  </Link>
                </li>
              )}
            </ul>
            <ul className={listClass}>
              <li className={listItemClass}>
                <Link href={MENU_LINKS.STORIES} prefetch={false}>
                  Stories
                </Link>
              </li>
              <li className={listItemClass}>
                <Link href='/recipes' prefetch={false}>
                  Recipes
                </Link>
              </li>
              <li className={listItemClass}>
                <Link href='/services' prefetch={false}>
                  Services
                </Link>
              </li>
            </ul>
            <ul className={listClass}>
              <li className={listItemClass}>
                <Link href='/about-us' prefetch={false}>
                  About Us
                </Link>
              </li>
              <li className={listItemClass}>
                <Link href={MENU_LINKS.CONTACT_US} prefetch={false}>
                  Contact Us
                </Link>
              </li>
            </ul>
            <ul className={listClass}>
              <li className={listItemClass}>
                <Link href='/policies/shipping-policy' prefetch={false}>
                  Shipping Policy
                </Link>
              </li>
              <li className={listItemClass}>
                <Link href='/policies/returns-refunds-policy' prefetch={false}>
                  Return and Refund Policy
                </Link>
              </li>
              <li className={listItemClass}>
                <Link href='/faqs' prefetch={false}>
                  FAQs
                </Link>
              </li>
              <li className={listItemClass}>
                <Link
                  href='/policies/weekend-of-plenty-terms-and-conditions'
                  prefetch={false}>
                  Weekend of Plenty T&C
                </Link>
              </li>
            </ul>
          </div>
          <div className='mt-16 flex w-full items-start justify-between overflow-x-hidden px-4 max-lg:mx-auto max-lg:flex-col max-lg:gap-y-8'>
            <Link
              className='group flex basis-1/4 items-center max-[375px]:w-full'
              prefetch={false}
              href={`tel:${COUNTRY_CODE}${LANDLINE_NUMBER}`}>
              <div className='block aspect-square h-10 rounded-full border-2 border-white bg-transparent text-center'>
                <div className='flex h-full items-center justify-center'>
                  <Image
                    src={'/vectors/call.svg'}
                    alt='Call'
                    width={16}
                    height={16}
                  />
                </div>
              </div>
              <div className='ml-3 flex flex-col'>
                <p className={headerClass}>Call Us</p>
                <p className={subHeaderClass}>Mon-Sun: 10 am to 8 pm</p>
              </div>
            </Link>
            <WhatsappFooter />
            <Link
              prefetch={false}
              className='group flex basis-1/4 items-center max-[375px]:w-full'
              href='/form/personal-shopper/'>
              <div className='block aspect-square h-10 rounded-full border-2 border-white bg-transparent text-center'>
                <div className='flex h-full items-center justify-center'>
                  <Image
                    src={'/vectors/calendar.svg'}
                    alt='Calendar'
                    width={16}
                    height={16}
                  />
                </div>
              </div>
              <div className='ml-3 flex flex-col'>
                <p className={headerClass}>Book an Appointment</p>
                <p className={subHeaderClass}>Click here to choose a slot</p>
              </div>
            </Link>
            <div className='group flex basis-1/4 items-start max-[375px]:w-full'>
              <div className='block aspect-square h-10 rounded-full border-2 border-white bg-transparent text-center'>
                <div className='flex h-full items-center justify-center'>
                  <Image
                    src={'/vectors/location-white.svg'}
                    color='white'
                    alt='Location'
                    width={16}
                    height={16}
                  />
                </div>
              </div>
              <Link className='ml-3 flex w-full flex-col' href='/store-locator'>
                <p className='text-lg font-semibold'>Store Locator</p>
                <p className={subHeaderClass}>
                  Click here to find a store near you
                </p>
              </Link>
            </div>
          </div>
        </div>
        <div className='h-[0.05rem] w-full bg-white' />
        <div className='flex items-center justify-between pt-6 pb-4 max-lg:flex-col max-lg:items-center max-lg:justify-end'>
          <div className='ml-18 flex items-center gap-x-3 max-lg:order-2 max-lg:ml-0 max-lg:mt-4'>
            {/* <p className={socialClass}>Legal Notice</p>
            <div className='h-1 w-1 rounded-full bg-white' /> */}
            <p className={socialClass}>
              <Link prefetch={false} href='/policies/privacy-policy'>
                Privacy Policy
              </Link>
            </p>
            <p className={socialClass}>
              <Link prefetch={false} href='/sitemap'>
                Sitemap
              </Link>
            </p>
          </div>
          <div className='mr-18 flex items-center justify-end gap-x-4 max-lg:mx-auto max-[300px]:gap-x-2 sm:gap-x-8'>
            <a
              className={socialClass}
              href={FACEBOOK_URL}
              onClick={() =>
                pushGA4Event('social_click', { platform: 'facebook' })
              }
              target='_blank'
              rel='noreferrer'>
              FACEBOOK
            </a>
            <a
              className={socialClass}
              href={INSTAGRAM_URL}
              onClick={() =>
                pushGA4Event('social_click', { platform: 'instagram' })
              }
              target='_blank'
              rel='noreferrer'>
              INSTAGRAM
            </a>
            <a
              className={socialClass}
              href={YOUTUBE_URL}
              onClick={() =>
                pushGA4Event('social_click', { platform: 'youtube' })
              }
              target='_blank'
              rel='noreferrer'>
              YOUTUBE
            </a>
            {/* <a
              className={socialClass}
              href={`https://api.whatsapp.com/send?phone=91${WHATSAPP_NUMBER}&text=Hi&utm_source=Whatsapp&utm_medium=Clevetap&utm_campaign=Whatsapp_icon_web&utm_term=030624`}
              target='_blank'
              rel='noreferrer'>
              WHATSAPP
            </a> */}
          </div>
        </div>
        <div className='text-center text-xs lg:text-sm pb-4'>
          <p>
            &copy; {new Date().getFullYear()} Foodstories Pvt. Ltd. All rights
            reserved.
          </p>
        </div>
        <div className='flex h-max w-full items-center justify-center bg-dew py-3'>
          <div className='w-full text-center'>
            <Text className='text-[10px] font-normal text-[#640C10] md:text-xs md:font-bold'>
              {cookieText}
              <Link
                href='/policies/privacy-policy/'
                prefetch={false}
                className='underline'>
                cookie policy.
              </Link>
            </Text>
          </div>
        </div>
      </footer>
    );
  }
  if (type === HEADER_FOOTER_TYPES.hidden) {
    return <></>;
  }
  return (
    <footer
      className={twMerge(
        'bg-[#EDEAE980]',
        addBottomPaddingCheckout && 'max-lg:pb-20'
      )}>
      <Container className='py-6'>
        <div className='flex w-full flex-col gap-6 px-3'>
          <Logo rootClasses='mx-auto' />
          <div className='flex flex-col items-center justify-center gap-4'>
            <div className='text-lg font-semibold'>Customer Service Team</div>
            <div className='flex flex-col items-center justify-center gap-4 md:flex-row md:gap-10'>
              <div className='text-sm'>
                Tel:{' '}
                <Link
                  href={`tel:${COUNTRY_CODE}${LANDLINE_NUMBER}`}
                  className={anchorClass}>
                  {COUNTRY_CODE} {LANDLINE_NUMBER}
                </Link>
              </div>
              <div className='text-sm'>
                Whatsapp:{' '}
                <Link
                  href={`https://api.whatsapp.com/send?phone=91${WHATSAPP_NUMBER}&text=Hi&utm_source=Whatsapp&utm_medium=Clevetap&utm_campaign=Whatsapp_icon_web&utm_term=030624`}
                  target='_blank'
                  rel='noreferrer'
                  className={anchorClass}>
                  {COUNTRY_CODE} {WHATSAPP_NUMBER}
                </Link>
              </div>
              <div className='text-sm'>
                Email:{' '}
                <Link
                  href={`mailto:${EMAIL_ID_SUPPORT}`}
                  className={anchorClass}>
                  {EMAIL_ID_SUPPORT}
                </Link>
              </div>
              <div className='text-sm'>
                <Link
                  href='/faqs'
                  target='_blank'
                  className={anchorClass}
                  prefetch={false}>
                  FAQs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
